import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { idsFor } from "../lib/modules";

// Scan links. Note the leading slash on these paths: the scan-link routes are
// registered as /s2/api/... while the template and organization routes are s2/api/...
// with no slash. They are not interchangeable.
export function useScanLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // { id, action }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/s2/api/scan-links");
      setLinks(data?.links || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load scan links.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async ({ domain, groups, organizationId, instantStart }) => {
    const data = await api.post("/s2/api/scan-links", {
      target: domain,
      targetType: "domain",
      modules: idsFor(groups),
      // Not user-editable: the client fixes this at seven days.
      expiresIn: 604800,
      instantStart,
      oneTime: true,
      organizationId,
    });
    await load();
    return data?.link;
  }, [load]);

  const revoke = useCallback(async (id) => {
    setBusy({ id, action: "revoke" });
    try { await api.post("/s2/api/scan-links/revoke", { link: id }); await load(); }
    finally { setBusy(null); }
  }, [load]);

  const regenerate = useCallback(async (id) => {
    setBusy({ id, action: "regenerate" });
    try { const d = await api.post("/s2/api/scan-links/regenerate", { link: id }); await load(); return d?.fullLink; }
    finally { setBusy(null); }
  }, [load]);

  const remove = useCallback(async (id) => {
    setBusy({ id, action: "delete" });
    try { await api.del("/s2/api/scan-links", { link: id }); setLinks((p) => p.filter((l) => l.id !== id)); }
    finally { setBusy(null); }
  }, []);

  return { links, loading, error, busy, reload: load, create, revoke, regenerate, remove };
}
